import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import mime from 'mime-types'
import { sentenceCase, capitalCase } from 'change-case'
import { CloudinaryImage } from '@cloudinary/url-gen'
import Image from '@/components/Image'
import debounce from 'lodash/debounce'
import 'cropperjs/dist/cropper.css'
import { useSnackbar } from 'notistack'
import Cropper, { ReactCropperElement } from 'react-cropper'
import { LoadingButton } from '@mui/lab'
import { styled } from '@mui/material/styles'
import {
  Box,
  Button,
  Dialog,
  InputAdornment,
  AppBar,
  Slide,
  IconButton,
  Toolbar,
  Grid,
  Stack,
} from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import Iconify from '@/components/Iconify'
import { FormProvider, RHFTextField, RHFSelect } from '@/components/hook-form'
import { downloadZip } from '@/utils/imageDownload'

const THUMB_SIZE = 64

enum SlicesEnum {
  One = 'One',
  Two = 'Two',
  FourSquares = 'FourSquares',
}

interface ImageOptions {
  width: number
  height: number
  rotate: number
  slices: SlicesEnum
  x: number
  y: number
}

type FormValuesProps = ImageOptions

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

const RootStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(3, 2),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3, 2),
  },
}))

interface Props {
  cloudinaryPublicId: string
  name?: string
  contentType?: string
  disabledDialogs?: boolean
  disabledDownload?: boolean
  onEdit?: (imagesUri: string[], contentType: string) => void
}

const ImageEditorDialogs = ({
  cloudinaryPublicId,
  contentType = 'image/png',
  name,
  disabledDialogs = false,
  disabledDownload = false,
  onEdit,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar()
  const [open, setOpen] = useState(false)
  const cropperRef = useRef<ReactCropperElement>(null)

  const myImage = new CloudinaryImage(
    `${cloudinaryPublicId}.${mime.extension(contentType)}`,
    {
      cloudName: 'ddr2pzcro',
    },
    {
      signUrl: true,
    }
  )

  const defaultValues = useMemo<ImageOptions>(
    () => ({
      width: 0,
      height: 0,
      rotate: 0,
      x: 1,
      y: 1,
      slices: SlicesEnum.One,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const methods = useForm<FormValuesProps>({
    defaultValues,
  })

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods

  const values = watch()

  const debounceSetValueByCropperData = useCallback(
    debounce((imageData: Cropper.Data) => {
      setValue('width', Math.floor(imageData.width))
      setValue('height', Math.floor(imageData.height))
      setValue('rotate', imageData.rotate)
      setValue('x', imageData.x)
      setValue('y', imageData.y)
    }, 500),
    []
  )

  const cropImage = (option: keyof ImageOptions, value: number) => {
    const cropper = cropperRef.current?.cropper
    if (!cropper) return
    cropper.setData({ [option]: value })
  }

  const handleOptionChange = (option: keyof ImageOptions, value: string) => {
    const num = parseInt(value, 10) || 0
    cropImage(option, num)
    setValue(option, num)
  }

  const sliceImage = useCallback(
    (_cropper: Cropper, _slices: SlicesEnum, _contentType: string): string[] => {
      const croppedCanvas = _cropper.getCroppedCanvas()
      const slicesResult: string[] = []
      try {
        if (_slices === SlicesEnum.One) {
          slicesResult.push(croppedCanvas.toDataURL(_contentType))
        } else if (_slices === SlicesEnum.Two) {
          const width = croppedCanvas.width / 2
          const height = croppedCanvas.height
          for (let i = 0; i < 2; i++) {
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const context = canvas.getContext('2d')
            if (context) {
              context.drawImage(croppedCanvas, -i * width, 0)
              slicesResult.push(canvas.toDataURL(_contentType))
            }
          }
        } else if (_slices === SlicesEnum.FourSquares) {
          const width = croppedCanvas.width / 2
          const height = croppedCanvas.height / 2
          for (let i = 0; i < 2; i++) {
            for (let j = 0; j < 2; j++) {
              const canvas = document.createElement('canvas')
              canvas.width = width
              canvas.height = height
              const context = canvas.getContext('2d')
              if (context) {
                context.drawImage(croppedCanvas, -j * width, -i * height)
                slicesResult.push(canvas.toDataURL(_contentType))
              }
            }
          }
        }
      } catch (error) {
        console.error(error)
      }
      return slicesResult
    },
    []
  )

  const imagesUri = useMemo(() => {
    const cropper = cropperRef.current?.cropper
    if (!cropper) return []
    return sliceImage(cropper, values.slices, contentType)
  }, [contentType, sliceImage, values.slices])

  const download = (imageUri: string) => {
    const downloadLink = document.createElement('a')
    downloadLink.href = imageUri
    downloadLink.download = `${name}.${mime.extension(contentType)}`
    downloadLink.click()
  }

  const onSubmit = async (data: FormValuesProps) => {
    try {
      if (imagesUri.length === 1) {
        download(imagesUri[0])
      } else {
        await downloadZip(
          imagesUri.map((imageUri) => ({ imageUri, contentType })),
          name
        )
      }
      await new Promise((resolve) => setTimeout(resolve, 500))
      enqueueSnackbar('Download success!')
    } catch (error) {
      console.error(error)
    }
  }
  const onCrop = () => {
    const cropper = cropperRef.current?.cropper
    const imageData = cropper?.getData()
    if (!imageData) return
    debounceSetValueByCropperData(imageData)
  }

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  useEffect(() => {
    onEdit?.(imagesUri, contentType)
  }, [contentType, imagesUri, onEdit])

  const dialogsContent = (
    <Grid container spacing={3} mt={0}>
      <Grid
        item
        xs={12}
        sm={6}
        md={8}
        sx={{
          height: {
            sm: '30rem',
            md: '35rem',
            lg: '35rem',
            xl: '80vh',
          },
        }}
      >
        <Cropper
          ref={cropperRef}
          src={myImage.toURL()}
          style={{
            height: '100%',
            width: '100%',
            borderRadius: '1rem',
            overflow: 'hidden',
          }}
          background={false}
          viewMode={1}
          guides={false}
          dragMode="move"
          autoCropArea={1}
          crop={onCrop}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <RHFTextField
              name="width"
              label="Width"
              placeholder="Width"
              onChange={(e) => handleOptionChange('width', e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: <InputAdornment position="start">px</InputAdornment>,
                type: 'number',
              }}
            />
            <RHFTextField
              name="height"
              label="Height"
              placeholder="Height"
              onChange={(e) => handleOptionChange('height', e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: <InputAdornment position="start">px</InputAdornment>,
                type: 'number',
              }}
            />

            <RHFTextField
              name="rotate"
              label="Rotate"
              placeholder="Rotate"
              onChange={(e) => handleOptionChange('rotate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: <InputAdornment position="start">°</InputAdornment>,
                type: 'number',
              }}
            />
            <RHFSelect name="slices" label="Slices">
              {Object.values(SlicesEnum).map((slice) => (
                <option key={slice} value={slice}>
                  {capitalCase(sentenceCase(slice))}
                </option>
              ))}
            </RHFSelect>
            {values.slices !== SlicesEnum.One && cropperRef.current?.cropper && (
              <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
                {imagesUri.map((img) => (
                  <Box key={img} sx={{ px: 0.75 }}>
                    <Image
                      disabledEffect
                      alt="thumb image"
                      src={img}
                      sx={{
                        width: THUMB_SIZE,
                        height: THUMB_SIZE,
                        borderRadius: 1.5,
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            )}
            {!disabledDownload && (
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                loading={isSubmitting}
              >
                Download
              </LoadingButton>
            )}
          </Stack>
        </FormProvider>
      </Grid>
    </Grid>
  )
  return (
    <>
      {disabledDialogs ? (
        dialogsContent
      ) : (
        <>
          <Button variant="outlined" fullWidth onClick={handleClickOpen}>
            Edit and Download
          </Button>

          <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition}>
            <AppBar sx={{ position: 'relative' }}>
              <Toolbar>
                <IconButton color="inherit" edge="start" onClick={handleClose}>
                  <Iconify icon="eva:close-fill" />
                </IconButton>
                {name ?? ''}
              </Toolbar>
            </AppBar>
            <RootStyle>{dialogsContent}</RootStyle>
          </Dialog>
        </>
      )}
    </>
  )
}

export default ImageEditorDialogs
